'use client';

import {
  MenuCoachmarkPinPersistStep,
  MenuCoachmarkPinStep,
} from '@/app-shell/layout/Navigation/coachmark';
import { EvidencePanelAction } from '@/app-shell/layout/Navigation/sidebar-panel/panels/evidence/evidence-panel-action';
import { SessionsPanelAction } from '@/app-shell/layout/Navigation/sidebar-panel/panels/sessions/sessions-panel-action';
import type { SidebarPanelContent } from '@/atoms/sidebarPanelAtoms';
import { Button } from '@heidi/design-system/button';
import { Kbd } from '@heidi/design-system/kbd';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { TypographyCaption } from '@heidi/design-system/typography';
import { isMac } from '@heidi/utils';
import { memo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Key } from 'ts-key-enum';

const messages = defineMessages({
  pinPanel: {
    defaultMessage: 'Pin panel',
    id: 'VtlB5/zxqC',
    description: 'Sidebar panel toggle that keeps the panel open',
  },
  unpinPanel: {
    defaultMessage: 'Unpin panel',
    id: 'aB2zjCjL0b',
    description: 'Sidebar panel toggle that lets the panel hide again',
  },
  pinPanelDescription: {
    defaultMessage: 'Prevent panel from hiding automatically',
    id: 'AifuTKNf9d',
    description: 'Tooltip detail explaining what pinning the sidebar panel does',
  },
  unpinPanelDescription: {
    defaultMessage: 'Panel will hide automatically when not in use',
    id: 'aV5UDs2Bck',
    description: 'Tooltip detail explaining what unpinning the sidebar panel does',
  },
});

interface PanelPinIconProps {
  className?: string;
  isPinned: boolean;
}

/** Panel pin icon with inner bar that toggles between pinned/unpinned states */
function PanelPinIcon({ className, isPinned }: PanelPinIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {/* Outer border */}
      <rect width="18" height="18" x="3" y="3" rx="3" />
      {/* Inner bar — wide when pinned, narrow when unpinned */}
      <rect
        x="6"
        y="6"
        width={isPinned ? 6 : 2}
        height="12"
        rx="1.5"
        fill="currentColor"
        stroke="none"
      />
    </svg>
  );
}

export interface PanelHeaderProps {
  /** Current content type being displayed */
  content: SidebarPanelContent;
  /** Whether the sidebar is pinned */
  isPinned: boolean;
  /** Callback to toggle pin state */
  onTogglePin: () => void;
  /** Whether to show the pin/unpin toggle (hidden on pages without panel content) */
  showPinToggle: boolean;
}

/**
 * Header for the sidebar panel.
 * Shows context-aware "Create" action on the left and pin/unpin toggle on the right.
 */
export const PanelHeader = memo(function PanelHeader({
  content,
  isPinned,
  onTogglePin,
  showPinToggle,
}: PanelHeaderProps) {
  const intl = useIntl();

  const pinLabel = intl.formatMessage(isPinned ? messages.unpinPanel : messages.pinPanel);

  const pinDescription = intl.formatMessage(
    isPinned ? messages.unpinPanelDescription : messages.pinPanelDescription
  );

  return (
    <div className="shrink-0 pb-2 pt-4">
      <div className="flex h-9 items-center justify-between">
        {/* Left: Context-aware action button */}
        <div className="flex items-center">
          {content === 'sessions' && <SessionsPanelAction />}
          {content === 'evidence' && <EvidencePanelAction />}
        </div>

        {/* Right: Pin/Unpin toggle — hidden on pages without panel content */}
        {showPinToggle && (
          <MenuCoachmarkPinPersistStep>
            <MenuCoachmarkPinStep>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="panel_ghost"
                    size="sm"
                    iconOnly
                    onClick={onTogglePin}
                    aria-label={pinLabel}
                    aria-pressed={isPinned}
                  >
                    <PanelPinIcon isPinned={isPinned} className="!size-4 shrink-0" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent
                  side="right"
                  align="start"
                  className="flex flex-col gap-0.5 px-2.5 py-1.5"
                >
                  <div className="flex items-center">
                    <TypographyCaption className="text-text-primary-on-dark">
                      {pinLabel}
                    </TypographyCaption>
                    <div className="ms-2 flex items-center gap-0.5">
                      <Kbd
                        keyType="modifier"
                        isMac={isMac}
                        size="compact"
                        className="border-text-primary-on-dark/20 bg-text-primary-on-dark/10"
                        contentClassName="text-text-primary-on-dark/60"
                      >
                        {Key.Meta}
                      </Kbd>
                      <Kbd
                        size="compact"
                        className="border-text-primary-on-dark/20 bg-text-primary-on-dark/10"
                        contentClassName="text-text-primary-on-dark/60"
                      >
                        /
                      </Kbd>
                    </div>
                  </div>
                  <TypographyCaption className="text-text-primary-on-dark/60">
                    {pinDescription}
                  </TypographyCaption>
                </TooltipContent>
              </Tooltip>
            </MenuCoachmarkPinStep>
          </MenuCoachmarkPinPersistStep>
        )}
      </div>
    </div>
  );
});
